import { useCallback, useEffect, useRef } from 'react';

/**
 * A debounced function that can be canceled.
 * It also includes a property to identify it as debounced.
 */
export type DebouncedFunction<T extends (...args: any[]) => void> = {
  /**
   * The debounced function.
   * @param args The arguments to pass to the original callback.
   */
  (...args: Parameters<T>): void;
};

/**
 * A custom React hook that creates a debounced version of a callback function.
 *
 * @template T - The type of the callback function.
 * @param {T} callback - The function to debounce. This function will only be
 * called after the specified delay has passed without any new calls.
 * @param {number} delay - The debounce delay in milliseconds.
 * @returns {{
 * start: DebouncedFunction<T>;
 * cancel: () => void;
 * }} An object containing the debounced `start` function and a `cancel` function.
 *
 * @example
 * const MyComponent = () => {
 * const { start: debouncedLog } = useDebounce((message) => {
 * console.log(message);
 * }, 500);
 *
 * return <button onClick={() => debouncedLog('Hello!')}>Log Message</button>;
 * };
 */
const useDebounce = <T extends (...args: any[]) => void>(
  callback: T,
  delay: number
): { start: DebouncedFunction<T>; cancel: () => void } => {
  // useRef to store the timeout ID across re-renders without causing them.
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  /**
   * Clears the scheduled timeout, preventing the callback from executing.
   */
  const cancel = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null; // Clear the ref after canceling.
    }
  }, []);

  /**
   * The debounced version of the callback. Each call resets the timer.
   */
  const start = useCallback(
    (...args: Parameters<T>) => {
      cancel();

      timeoutRef.current = setTimeout(() => {
        callback(...args);
      }, delay);
    },
    [callback, cancel, delay]
  );

  useEffect(() => {
    return () => {
      cancel();
    };
  }, [cancel]);

  return { start: start as DebouncedFunction<T>, cancel };
};

export default useDebounce;
